// src/main.tsx

// Initialize Sentry as early as possible
import { initSentry } from './config/sentry';
initSentry();

import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ErrorBoundary } from '@sentry/react'
import App from './App'
import { DarkModeProvider } from './context/DarkModeContext'
import { AuthProvider } from './context/AuthContext'
import { ToastProvider } from './context/ToastContext'
import { SettingsProvider } from './context/SettingsContext'
import { networkUtils } from './utils/networkUtils'
import { createLogger } from './utils/logger'
import { initializeApp } from './services/appInitialization'
// Using polling-based notifications
import './index.css'

// Initialize network utilities
networkUtils.init();

// Defer app initialization until after authentication is established
// This prevents unnecessary initialization on unauthenticated pages
let appInitialized = false;

const initializeAppWhenAuthenticated = async () => {
  if (appInitialized) return;
  
  try {
    const { data: { session } } = await import('./utils/supabase').then(m => m.supabase.auth.getSession());
    if (session) {
      appInitialized = true;
      
      // Token refresh handled by Supabase auth automatically
      
      await initializeApp();
      logger.debug('App initialization completed (including NotificationsV2 and event processing)');
    }
  } catch (error) {
    console.warn('Failed to check auth for app initialization:', error);
  }
};

// Set up auth state listener to initialize app when user logs in
import('./utils/supabase').then(({ supabase }) => {
  supabase.auth.onAuthStateChange((event, session) => {
    if (event === 'SIGNED_IN' && session && !appInitialized) {
      initializeAppWhenAuthenticated();
    } else if (event === 'SIGNED_OUT' && appInitialized) {
      // Clean up when user signs out
      appInitialized = false;
      
      import('./services/appInitialization').then(({ shutdownApp }) => {
        shutdownApp().catch(error => {
          console.warn('Failed to shutdown app:', error);
        });
      });
    }
  });
  
  // Check if already authenticated on load
  initializeAppWhenAuthenticated();
});

const logger = createLogger({ module: 'Main', level: 'info' });

// Create a query client with optimized settings
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (was cacheTime)
      retry: (failureCount, error) => {
        // Don't retry on 4xx errors
        if (error && typeof error === 'object' && 'status' in error) {
          const status = (error as any).status;
          if (status >= 400 && status < 500) {
            return false;
          }
        }
        return failureCount < 3;
      },
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
  },
});

// First, perform a synchronous check before any rendering to catch MFA flags early
// This prevents any flashes of the main app content if MFA verification is needed
function synchronousMfaCheck() {
  try {
    // Check MFA flags synchronously before any rendering
    const mfaPendingInStorage = sessionStorage.getItem('mfa_verification_pending') === 'true';
    const mfaSignInInProgress = sessionStorage.getItem('mfa_signin_in_progress') === 'true';
    const globalFlagSet = window.__MFA_SIGNIN_STARTED === true;
    const mfaFlagSetTime = sessionStorage.getItem('mfa_flags_set_time');
    const timeValid = mfaFlagSetTime ? (Date.now() - parseInt(mfaFlagSetTime)) < 3600000 : false; // Valid for 1 hour
    
    // If MFA flags are active and we're not already on the verification page, redirect instantly
    if ((mfaPendingInStorage || mfaSignInInProgress || globalFlagSet) && 
        window.location.pathname !== '/verify-mfa' &&
        (timeValid || !mfaFlagSetTime)) { // Either the time is valid or there's no timestamp (backward compatibility)
      
      console.log('[MFAGuard] MFA detected in synchronous check, redirecting immediately');
      
      // Prevent any rendering at all by redirecting synchronously
      window.location.replace('/verify-mfa');
      
      // Return true to indicate a redirect happened
      return true;
    }
  } catch (e) {
    console.error('[MFAGuard] Error in synchronous MFA check:', e);
  }
  
  return false;
}

// Run synchronous check immediately before any React rendering
const redirectedSynchronously = synchronousMfaCheck();

// React component for MFA loading screen
const MFALoadingScreen: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center bg-blue-500">
    <div className="text-center">
      <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-white mx-auto mb-4"></div>
      <p className="text-white text-lg">Preparing verification...</p>
      <p className="text-white text-sm mt-2">Please wait while we secure your account...</p>
    </div>
  </div>
);

// Add MFA guard container to block any rendering if MFA is detected
// This is the earliest possible point we can intercept the rendering process
const MFAGuard: React.FC<{children: React.ReactNode}> = ({ children }) => {
  const [shouldRedirect, setShouldRedirect] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);
  const redirectTimeoutRef = React.useRef<number | null>(null);
  const checkIntervalRef = React.useRef<number | null>(null);
  const lastPathRef = React.useRef<string>(window.location.pathname);
  
  // Function to clear MFA protection
  const clearMfaProtection = React.useCallback(() => {
    try {
      logger.debug('Clearing any remaining MFA protection during navigation');
      
      // Clear all session storage flags - do this FIRST for maximum reliability
      sessionStorage.removeItem('mfa_signin_in_progress');
      sessionStorage.removeItem('mfa_verification_pending');
      sessionStorage.removeItem('mfa_factor_id');
      sessionStorage.removeItem('mfa_email');
      sessionStorage.removeItem('mfa_ultra_protection');
      sessionStorage.removeItem('mfa_verification_completed');
      sessionStorage.removeItem('mfa_flags_set_time');
      sessionStorage.removeItem('mfa_verification_start_time');
      
      // Also clear auth flow flags that might cause loading screens
      sessionStorage.removeItem('auth_flow_in_progress');
      sessionStorage.removeItem('auth_flow_email');
      sessionStorage.removeItem('auth_flow_timestamp');
      
      // Clear window flags
      if (typeof window !== 'undefined') {
        window.__MFA_SIGNIN_STARTED = false;
        window.__mfaClickListenerAdded = false;
      }
      
      logger.debug('MFA protection fully cleared during navigation');
    } catch (e) {
      logger.error('Error clearing MFA protection:', e);
    }
  }, []);
  
  // Check for MFA pending state on mount
  React.useEffect(() => {
    // Special check for recent page reload due to non-MFA login
    const authCompleted = sessionStorage.getItem('auth_completed') === 'true';
    if (authCompleted) {
      // If we're seeing this flag, it means we just completed a non-MFA login and reloaded
      // We should skip all MFA protection code to ensure a clean experience
      logger.debug('Detected non-MFA login completion, bypassing all MFA guards');
      setIsLoading(false);
      // Skip remaining MFA checks
      return;
    }
    
    // Normal MFA Guard initialization for MFA users
    const mfaPendingInStorage = sessionStorage.getItem('mfa_verification_pending') === 'true';
    const mfaFactorId = sessionStorage.getItem('mfa_factor_id');
    
    const mfaPending = mfaPendingInStorage && mfaFactorId;
    
    // Log the MFA state
    logger.debug('MFA check on mount', {
      mfaPendingInStorage,
      hasMfaFactorId: !!mfaFactorId,
      mfaPending,
      location: window.location.pathname
    });
    
    // If MFA is pending and we're not on the MFA verification page, redirect
    if (mfaPending && window.location.pathname !== '/verify-mfa') {
      setShouldRedirect(true);
      
      // Schedule a redirect
      redirectTimeoutRef.current = window.setTimeout(() => {
        window.location.replace('/verify-mfa');
      }, 100);
    } else {
      // No MFA pending, allow rendering
      setIsLoading(false);
    }
  }, []);
  
  // Add a navigation listener that clears MFA protection when navigating to /home
  React.useEffect(() => {
    // Monitor popstate events which occur when navigating with browser back/forward buttons
    const handlePopState = () => {
      const currentPath = window.location.pathname;
      if (currentPath === '/home') {
        logger.debug('Detected navigation to /home via popstate, ensuring MFA protection is cleared');
        clearMfaProtection();
      }
    };
    
    // Monitor click events on anchor tags
    const handleClick = (e: MouseEvent) => {
      if (e.target instanceof HTMLAnchorElement) {
        const href = e.target.getAttribute('href');
        if (href && href.includes('/home')) {
          logger.debug('Detected click on link to /home, preparing to clear MFA protection');
          // Clear MFA protection before the navigation happens
          clearMfaProtection();
        }
      }
    };
    
    // Use a MutationObserver to detect URL changes more efficiently
    const observer = new MutationObserver(() => {
      const currentPath = window.location.pathname;
      if (currentPath === '/home' && lastPathRef.current !== currentPath) {
        logger.debug('Detected navigation to /home via URL change, ensuring MFA protection is cleared');
        clearMfaProtection();
      }
      lastPathRef.current = currentPath;
    });
    
    // Observe changes to the document that might indicate navigation
    observer.observe(document, { subtree: true, childList: true });
    
    window.addEventListener('popstate', handlePopState);
    document.addEventListener('click', handleClick, true);
    
    return () => {
      observer.disconnect();
      window.removeEventListener('popstate', handlePopState);
      document.removeEventListener('click', handleClick, true);
    };
  }, [clearMfaProtection]);
  
  // Check for beta token early to preserve it during auth state changes
  React.useEffect(() => {
    try {
      // Save the existing beta token to sessionStorage to recover it if needed
      const existingBetaToken = localStorage.getItem('beta_access_token');
      if (existingBetaToken) {
        sessionStorage.setItem('previous_beta_token', existingBetaToken);
        logger.debug('Saved existing beta token for recovery if needed', {
          tokenLength: existingBetaToken.length
        });
      }
    } catch (e) {
      console.error('Error saving beta token to session storage:', e);
    }
  }, []);
  
  // Check MFA flags in a separate function for reuse
  const checkMfaFlags = React.useCallback(() => {
    try {
      // Check for MFA indicators
      const mfaPendingInStorage = sessionStorage.getItem('mfa_verification_pending') === 'true';
      const mfaSignInInProgress = sessionStorage.getItem('mfa_signin_in_progress') === 'true';
      const globalFlagSet = window.__MFA_SIGNIN_STARTED === true;
      const mfaFactorId = sessionStorage.getItem('mfa_factor_id');
      const mfaFlagSetTime = sessionStorage.getItem('mfa_flags_set_time');
      
      // Add time-based validation - flags older than 1 hour are considered stale
      const timeValid = mfaFlagSetTime ? (Date.now() - parseInt(mfaFlagSetTime)) < 3600000 : false;
      
      // If any MFA flag is active and we're not already on the verification page
      // Also check that either the time is valid or there's no timestamp (backward compatibility)
      const needsRedirect = (mfaPendingInStorage || mfaSignInInProgress || globalFlagSet) && 
                           window.location.pathname !== '/verify-mfa' &&
                           (timeValid || !mfaFlagSetTime);
      
      if (needsRedirect) {
        logger.debug('MFA detected at root level - blocking app render', {
          mfaPendingInStorage,
          mfaSignInInProgress,
          globalFlagSet,
          hasFactorId: !!mfaFactorId,
          mfaFlagSetTime: mfaFlagSetTime ? new Date(parseInt(mfaFlagSetTime)).toISOString() : null,
          timeSinceSet: mfaFlagSetTime ? Math.round((Date.now() - parseInt(mfaFlagSetTime)) / 1000) + 's' : 'n/a',
          currentPath: window.location.pathname
        });
        
        // Prevent any rendering of the main App
        setShouldRedirect(true);
        
        // Clear any existing timeout to avoid multiple redirects
        if (redirectTimeoutRef.current) {
          window.clearTimeout(redirectTimeoutRef.current);
        }
        
        // Use a small timeout to ensure we don't get into a redirect loop
        redirectTimeoutRef.current = window.setTimeout(() => {
          // Force redirect - use replace for cleaner history
          window.location.replace('/verify-mfa');
        }, 50);
        
        return true;
      }
      
      return false;
    } catch (e) {
      logger.error('Error checking MFA flags:', e);
      return false;
    }
  }, []);
  
  React.useEffect(() => {
    // If we already redirected synchronously, don't do any further processing
    if (redirectedSynchronously) {
      return;
    }
    
    // Check if beta token was lost during page refresh
    try {
      const previousBetaToken = sessionStorage.getItem('previous_beta_token');
      const currentBetaToken = localStorage.getItem('beta_access_token');
      
      // If we lost the beta token during page refresh but have one saved, restore it
      if (previousBetaToken && !currentBetaToken) {
        logger.warn('Beta token lost during refresh, restoring from session storage');
        localStorage.setItem('beta_access_token', previousBetaToken);
      }
    } catch (e) {
      logger.error('Error checking/restoring beta token:', e);
    }
    
    // Immediate check on mount
    const needsRedirect = checkMfaFlags();
    
    // Only proceed if no redirect needed
    if (!needsRedirect) {
      setIsLoading(false);
      
      // Only set up monitoring if we're on certain pages where MFA might be triggered
      const shouldMonitor = window.location.pathname === '/login' || 
                           window.location.pathname === '/' ||
                           window.location.pathname === '/verify-email';
      
      if (shouldMonitor) {
        // Set up continuous monitoring for MFA flags
        // This ensures we catch MFA activation even after initial render
        checkIntervalRef.current = window.setInterval(() => {
          checkMfaFlags();
        }, 500);
      }
    }
    
    // Cleanup function
    return () => {
      // Clear any pending timeouts and intervals
      if (redirectTimeoutRef.current) {
        window.clearTimeout(redirectTimeoutRef.current);
      }
      
      if (checkIntervalRef.current) {
        window.clearInterval(checkIntervalRef.current);
      }
    };
  }, [checkMfaFlags]);
  
  // If redirect needed, show a loading indicator until the redirect happens
  if (shouldRedirect) {
    return <MFALoadingScreen />;
  }
  
  // Show a brief loading state while we check MFA flags
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  // Only render children if we've confirmed no MFA is active
  return <>{children}</>;
};

// Add global type declaration for MFA flag
declare global {
  interface Window {
    __MFA_SIGNIN_STARTED?: boolean;
    __mfaClickListenerAdded?: boolean;
    originalPushState?: History['pushState'];
    originalReplaceState?: History['replaceState'];
  }
}

// Don't render the app at all if we've already redirected synchronously
if (redirectedSynchronously) {
  // Use React to render the loading screen instead of direct DOM manipulation
  ReactDOM.createRoot(document.getElementById('root')!).render(
    <MFALoadingScreen />
  );
} else {
  // Normal app initialization
  const AppWrapper = (
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <SettingsProvider>
            <AuthProvider>
              <DarkModeProvider>
                <MFAGuard>
                  <ErrorBoundary fallback={
                    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
                      <div className="text-center">
                        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                          Something went wrong
                        </h1>
                        <p className="text-gray-600 dark:text-gray-400 mb-4">
                          We've been notified and are working on a fix.
                        </p>
                        <button
                          onClick={() => window.location.reload()}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                        >
                          Reload Page
                        </button>
                      </div>
                    </div>
                  } showDialog>
                    <App />
                  </ErrorBoundary>
                </MFAGuard>
              </DarkModeProvider>
            </AuthProvider>
          </SettingsProvider>
        </ToastProvider>
      </QueryClientProvider>
    </BrowserRouter>
  );

  // Only use StrictMode in development
  const root = document.getElementById('root')!;
  if (import.meta.env.DEV) {
    ReactDOM.createRoot(root).render(
      <React.StrictMode>
        {AppWrapper}
      </React.StrictMode>
    );
  } else {
    ReactDOM.createRoot(root).render(AppWrapper);
  }
}